import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useToggle, useUpdateEffect } from "react-use";
import { Button, IconButton, EditIcon, Tooltip, Label } from "@activecollab/components";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import { updateSubscribers, addSubscriber, removeSubscriber } from "~/store/actions/actions.subscription.jsx";
import {
  getParentEntity,
  getPossibleSubscribers,
  getSubscriberIdsByTypeAndId,
} from "~/store/selectors/selector.subscription.jsx";
import { getLoggedUser } from "~/store/selectors/selector.users.jsx";
import { SubscribersPicker } from "../tasks/TaskPanel/subscribers/SubscribersPicker.js";

const defaultDisplayedNumOfSubscribers = 5;

export const ObjectSubscribersPanel = ({ parentId, parentType, hiddenFromClients, members, canEdit }) => {
  const dispatch = useDispatch();

  const initialSubscriberIds = useSelector(
    (state) => getSubscriberIdsByTypeAndId(state, parentType, parentId),
    isEqual
  );
  const entity = useSelector((state) => getParentEntity(state, parentType, parentId));
  const [subscriberIds, setSubscriberIds] = useState(() => initialSubscriberIds);
  const [disabled, setDisabled] = useState(false);
  const [showAll, toggle] = useToggle(false);
  const loggedUser = useSelector(getLoggedUser);

  const { t } = useTranslation();

  const possibleSubscribers = useSelector((state) => getPossibleSubscribers(state, members));

  const possibleSubscribersIds = useMemo(() => {
    return possibleSubscribers.map((u) => u.id);
  }, [possibleSubscribers]);

  const handleChange = useCallback((data) => {
    setSubscriberIds(data);
  }, []);

  const handleSaveSubscribers = useCallback(() => {
    const first = [...initialSubscriberIds].sort(); //prevent direct mutation of state
    const second = [...subscriberIds].sort(); //prevent direct mutation of state
    if (!isEqual(first, second)) {
      dispatch(updateSubscribers(parentId, parentType, subscriberIds));
    }
  }, [initialSubscriberIds, subscriberIds, dispatch, parentId, parentType]);

  const loggedUserSubscribed = subscriberIds.includes(loggedUser.id);

  const users = useMemo(() => {
    return possibleSubscribers.filter((member) => {
      if (entity && typeof entity.is_hidden_from_clients !== "undefined" && entity.is_hidden_from_clients) {
        if (member.class === "Client") {
          return false;
        }
      }
      return subscriberIds.indexOf(member.id) > -1 && member.id !== loggedUser.id;
    });
  }, [entity, loggedUser.id, possibleSubscribers, subscriberIds]);

  const displayedUsers = useMemo(
    () =>
      users.length <= defaultDisplayedNumOfSubscribers + 1 || showAll
        ? users
        : users.slice(0, defaultDisplayedNumOfSubscribers),
    [showAll, users]
  );

  const subscribeLoggedUserToggle = useCallback(() => {
    setDisabled(true);
    loggedUserSubscribed
      ? dispatch(removeSubscriber(parentType, parentId, loggedUser.id)).then(() => setDisabled(false))
      : dispatch(addSubscriber(parentType, parentId, loggedUser.id)).then(() => setDisabled(false));
  }, [dispatch, loggedUser.id, loggedUserSubscribed, parentId, parentType]);

  useUpdateEffect(() => {
    setSubscriberIds((prevState) => {
      if (isEqual(prevState, initialSubscriberIds)) {
        return prevState;
      }
      return initialSubscriberIds;
    });
  }, [initialSubscriberIds]);

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <Label size="small">{t("Subscribers")}</Label>
        {canEdit ? (
          <SubscribersPicker
            onClose={handleSaveSubscribers}
            onChange={handleChange}
            hideClients={hiddenFromClients}
            selected={subscriberIds}
            userIds={possibleSubscribersIds}
            target={
              <span>
                <Tooltip title={t("Edit Subscribers")}>
                  <IconButton
                    variant="text gray"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </span>
            }
          />
        ) : null}
      </div>
      <ul
        className="tw-m-0 tw-p-0 tw-mb-2"
        data-testid="subscribers-list"
      >
        <li className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-mb-2">
          <div
            className={classNames("tw-flex tw-items-center tw-min-w-0", {
              "tw-opacity-50": !loggedUserSubscribed,
            })}
          >
            <UserLink
              id={loggedUser.id}
              size={24}
              className="tw-mr-2"
              notInteractive
              context="subscribers - logged user"
            />
          </div>
          <Button
            variant="text gray"
            size="small"
            onClick={subscribeLoggedUserToggle}
            className="tw-shrink-0"
            disabled={disabled}
          >
            {loggedUserSubscribed ? t("Unsubscribe") : t("Subscribe")}
          </Button>
        </li>
        {displayedUsers.map((user) => {
          return (
            <li
              key={user.id}
              className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-mb-2"
            >
              <div className="tw-flex tw-items-center tw-min-w-0">
                <UserLink
                  id={user.id}
                  size={24}
                  className="tw-mr-2"
                  notInteractive
                  context="subscribers"
                />
              </div>
            </li>
          );
        })}
      </ul>
      {users.length > defaultDisplayedNumOfSubscribers + 1 ? (
        <Button
          variant="tertiary"
          size="small"
          onClick={toggle}
          className="-tw-ml-2"
        >
          {showAll ? t("Show less") : `${t("Show")} ${users.length - defaultDisplayedNumOfSubscribers} ${t("more")}`}
        </Button>
      ) : null}
    </>
  );
};

ObjectSubscribersPanel.propTypes = {
  parentId: PropTypes.number.isRequired,
  parentType: PropTypes.string.isRequired,
  hiddenFromClients: PropTypes.bool,
  members: PropTypes.array.isRequired,
  canEdit: PropTypes.bool,
};

ObjectSubscribersPanel.defaultProps = {
  hiddenFromClients: false,
  members: [],
};
